// store/slices/consultationSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  // Session lifecycle
  startSessionThunk,
  getSessionThunk,
  pauseSessionThunk,
  resumeSessionThunk,
  completeSessionThunk,

  // Admin & branch views
  getAdminActiveSessionsThunk,
  getBranchSessionsThunk,

  // Cart operations
  addItemToCartThunk,
  removeItemFromCartThunk,
  updateItemQuantityThunk,
  showCartToCustomerThunk,

  // Pricing
  calculatePricingThunk,
  addPriceAdjustmentThunk,
  approvePriceAdjustmentThunk,
  lockPricesThunk,
  unlockPricesThunk,

  // Exchange items
  addExchangeItemThunk,
  adjustExchangeValueThunk,
  acceptExchangeItemThunk,

  // Events & timeline
  getSessionEventsThunk,
  getSessionTimelineThunk,

  // Order creation
  createOrderFromSessionThunk,

  // Actions
  clearSessionError,
  clearCurrentSession,
  clearSessionCart,
  clearSessionPricing,
  updateSessionNotesThunk,
  removeAdjustmentThunk,
  rejectPriceAdjustmentThunk,
  getPendingAdjustmentsThunk,
  editExchangeItemThunk,
  removeExchangeItemThunk,
  unacceptExchangeItemThunk,
} from "../thunks/consultationThunks";

const updateCapabilities = (session, priceLock) => {
  return {
    canModifyCart: session.status === "ACTIVE",
    canModifyPrices: session.status === "ACTIVE" && !priceLock?.isLocked,
    isPriceLocked: priceLock?.isLocked || false,
    requiresApproval: false,
    canComplete: ["ACTIVE", "PAUSED"].includes(session.status),
    canCreateOrder:
      ["ACTIVE", "PAUSED"].includes(session.status) && session.cartId !== null,
  };
};

const calculateSessionCapabilities = (session, priceLock = null) => {
  if (!session) {
    return {
      canModifyCart: false,
      canModifyPrices: false,
      isPriceLocked: false,
      requiresApproval: false,
      canComplete: false,
      canCreateOrder: false,
    };
  }

  const isActive = session.status === "ACTIVE";
  const isPaused = session.status === "PAUSED";
  const isLocked = priceLock?.isLocked || session.priceLock?.isLocked || false;

  return {
    canModifyCart: isActive,
    canModifyPrices: isActive && !isLocked,
    isPriceLocked: isLocked,
    requiresApproval: false,
    canComplete: isActive || isPaused,
    canCreateOrder: (isActive || isPaused) && session.cartId !== null,
  };
};

const initialState = {
  // Session data
  currentSession: null,
  sessions: [],
  activeSessions: [],

  // Session cart
  sessionCart: null,
  cartItems: [],

  // Session pricing
  sessionPricing: null,
  priceAdjustments: [],
  priceLockInfo: null,

  // Exchange items
  exchangeItems: [],

  // Events & timeline
  sessionEvents: [],
  sessionTimeline: [],

  // UI state
  loading: false,
  loadingSession: false,
  loadingCart: false,
  loadingPricing: false,
  loadingExchange: false,
  loadingEvents: false,
  error: null,

  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    pages: 0,
  },

  // Filters
  filters: {
    status: null,
    sessionType: null,
    fromDate: null,
    toDate: null,
    branchCode: null,
  },

  // Session capabilities
  sessionCapabilities: {
    canModifyCart: false,
    canModifyPrices: false,
    isPriceLocked: false,
    requiresApproval: false,
    canComplete: false,
    canCreateOrder: false,
  },

  pendingAdjustments: [],
  loadingPendingAdjustments: false,
};

const consultationSlice = createSlice({
  name: "consultation",
  initialState,
  reducers: {
    // Filter management
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Session selection
    setCurrentSession: (state, action) => {
      state.currentSession = action.payload;
    },

    // Session capabilities
    updateSessionCapabilities: (state, action) => {
      state.sessionCapabilities = {
        ...state.sessionCapabilities,
        ...action.payload,
      };
    },

    // Cart item selection
    selectCartItem: (state, action) => {
      state.selectedCartItem = action.payload;
    },

    // Exchange item selection
    selectExchangeItem: (state, action) => {
      state.selectedExchangeItem = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear actions
      .addCase(clearSessionError, (state) => {
        state.error = null;
      })
      .addCase(clearCurrentSession, (state) => {
        state.currentSession = null;
        state.sessionCart = null;
        state.sessionPricing = null;
        state.sessionEvents = [];
        state.sessionTimeline = [];
        state.sessionCapabilities = initialState.sessionCapabilities;
      })
      .addCase(clearSessionCart, (state) => {
        state.sessionCart = null;
        state.cartItems = [];
      })
      .addCase(clearSessionPricing, (state) => {
        state.sessionPricing = null;
        state.priceAdjustments = [];
      })

      // ========================================================================
      // SESSION LIFECYCLE
      // ========================================================================

      // Start session
      .addCase(startSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload.data.session;
        state.sessionCapabilities = {
          canModifyCart: true,
          canModifyPrices: true,
          isPriceLocked: false,
          requiresApproval: false,
          canComplete: true,
          canCreateOrder: false,
        };
      })
      .addCase(startSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get session
      .addCase(getSessionThunk.pending, (state) => {
        state.loadingSession = true;
        state.error = null;
      })
      .addCase(getSessionThunk.fulfilled, (state, action) => {
        state.loadingSession = false;

        const session = action.payload.data.session;
        const cart = action.payload.data.cart;
        const pricing = action.payload.data.pricing;
        const events = action.payload.data.events;

        state.currentSession = session;
        state.sessionCart = cart;
        state.sessionPricing = pricing; // Make sure this is set
        state.sessionEvents = events;

        // Update exchange items if present
        if (session.exchangeItems) {
          state.exchangeItems = session.exchangeItems;
        }

        // Update capabilities based on session state
        state.sessionCapabilities = calculateSessionCapabilities(
          session,
          session.priceLock
        );
      })
      .addCase(getSessionThunk.rejected, (state, action) => {
        state.loadingSession = false;
        state.error = action.payload;
      })

      // Pause session
      .addCase(pauseSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(pauseSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload.data.session;
        state.sessionCapabilities = calculateSessionCapabilities(
          action.payload.data.session
        );
      })

      .addCase(pauseSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Resume session
      .addCase(resumeSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resumeSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload.data.session;
        state.sessionCapabilities = calculateSessionCapabilities(
          action.payload.data.session
        );
      })

      .addCase(resumeSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Complete session
      .addCase(completeSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload.data.session;
        state.sessionCapabilities = {
          canModifyCart: false,
          canModifyPrices: false,
          isPriceLocked: true,
          requiresApproval: false,
          canComplete: false,
          canCreateOrder: false,
        };
      })
      .addCase(completeSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // ADMIN & BRANCH VIEWS
      // ========================================================================

      // Get admin active sessions
      .addCase(getAdminActiveSessionsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminActiveSessionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.activeSessions = action.payload.data.sessions;
      })
      .addCase(getAdminActiveSessionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get branch sessions
      .addCase(getBranchSessionsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBranchSessionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.data.sessions;
        if (action.payload.data.pagination) {
          state.pagination = action.payload.data.pagination;
        }
      })
      .addCase(getBranchSessionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // CART OPERATIONS
      // ========================================================================

      // Add item to cart
      .addCase(addItemToCartThunk.pending, (state) => {
        state.loadingCart = true;
        state.error = null;
      })
      .addCase(addItemToCartThunk.fulfilled, (state, action) => {
        state.loadingCart = false;
        state.sessionCart = action.payload.data.cart;
        // Clear pricing as it needs recalculation
        state.sessionPricing = null;
      })
      .addCase(addItemToCartThunk.rejected, (state, action) => {
        state.loadingCart = false;
        state.error = action.payload;
      })

      // Remove item from cart
      .addCase(removeItemFromCartThunk.pending, (state) => {
        state.loadingCart = true;
        state.error = null;
      })
      .addCase(removeItemFromCartThunk.fulfilled, (state, action) => {
        state.loadingCart = false;
        state.sessionCart = action.payload.data.cart;
        state.sessionPricing = null;
      })
      .addCase(removeItemFromCartThunk.rejected, (state, action) => {
        state.loadingCart = false;
        state.error = action.payload;
      })

      // Update item quantity
      .addCase(updateItemQuantityThunk.pending, (state) => {
        state.loadingCart = true;
        state.error = null;
      })
      .addCase(updateItemQuantityThunk.fulfilled, (state, action) => {
        state.loadingCart = false;
        state.sessionCart = action.payload.data.cart;
        state.sessionPricing = null;
      })
      .addCase(updateItemQuantityThunk.rejected, (state, action) => {
        state.loadingCart = false;
        state.error = action.payload;
      })

      // Show cart to customer
      .addCase(showCartToCustomerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(showCartToCustomerThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(showCartToCustomerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================================================
      // PRICING
      // ========================================================================

      // Calculate pricing
      .addCase(calculatePricingThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(calculatePricingThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.sessionPricing = action.payload.data.pricing;
      })
      .addCase(calculatePricingThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      })

      // Add price adjustment
      .addCase(addPriceAdjustmentThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(addPriceAdjustmentThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.currentSession = action.payload.data.session;
        state.sessionCapabilities.requiresApproval =
          action.payload.data.requiresApproval || false;
        // Clear pricing to force recalculation
        state.sessionPricing = null;
      })
      .addCase(addPriceAdjustmentThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      })

      // Approve price adjustment
      .addCase(approvePriceAdjustmentThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(approvePriceAdjustmentThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.currentSession = action.payload.data.session;
        state.sessionPricing = null;
      })
      .addCase(approvePriceAdjustmentThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      })

      // Lock prices
      .addCase(lockPricesThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(lockPricesThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.currentSession = action.payload.data.session;
        state.priceLockInfo = {
          isLocked: true,
          lockExpiresAt: action.payload.data.lockExpiresAt,
        };
        state.sessionCapabilities.isPriceLocked = true;
        state.sessionCapabilities.canModifyPrices = false;
      })
      .addCase(lockPricesThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      })
      .addCase(removeAdjustmentThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(removeAdjustmentThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.currentSession = action.payload.data.session;
        // Remove the adjustment from local state
        if (state.currentSession?.priceAdjustments) {
          state.currentSession.priceAdjustments =
            state.currentSession.priceAdjustments.filter(
              (adj) => adj.adjustmentId !== action.meta.arg.adjustmentId
            );
        }
        // Clear pricing to force recalculation
        state.sessionPricing = null;
      })
      .addCase(removeAdjustmentThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      })

      // Unlock prices
      .addCase(unlockPricesThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(unlockPricesThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.currentSession = action.payload.data.session;
        state.priceLockInfo = null;
        state.sessionCapabilities.isPriceLocked = false;
        state.sessionCapabilities.canModifyPrices = true;
      })
      .addCase(unlockPricesThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      })

      // ========================================================================
      // EXCHANGE ITEMS
      // ========================================================================

      // Add exchange item
      .addCase(addExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(addExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.currentSession = action.payload.data.session;
        state.exchangeItems = action.payload.data.session.exchangeItems;
        state.sessionPricing = null; // Recalculate pricing
      })
      .addCase(addExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Adjust exchange value
      .addCase(adjustExchangeValueThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(adjustExchangeValueThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.currentSession = action.payload.data.session;
        state.exchangeItems = action.payload.data.session.exchangeItems;
        state.sessionPricing = null;
      })
      .addCase(adjustExchangeValueThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Accept exchange item
      .addCase(acceptExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(acceptExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.currentSession = action.payload.data.session;
        state.exchangeItems = action.payload.data.session.exchangeItems;
        state.sessionPricing = null;
      })
      .addCase(acceptExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // ========================================================================
      // EVENTS & TIMELINE
      // ========================================================================

      // Get session events
      .addCase(getSessionEventsThunk.pending, (state) => {
        state.loadingEvents = true;
        state.error = null;
      })
      .addCase(getSessionEventsThunk.fulfilled, (state, action) => {
        state.loadingEvents = false;
        state.sessionEvents = action.payload.data.events;
      })
      .addCase(getSessionEventsThunk.rejected, (state, action) => {
        state.loadingEvents = false;
        state.error = action.payload;
      })

      // Get session timeline
      .addCase(getSessionTimelineThunk.pending, (state) => {
        state.loadingEvents = true;
        state.error = null;
      })
      .addCase(getSessionTimelineThunk.fulfilled, (state, action) => {
        state.loadingEvents = false;
        state.sessionTimeline = action.payload.data.timeline;
      })
      .addCase(getSessionTimelineThunk.rejected, (state, action) => {
        state.loadingEvents = false;
        state.error = action.payload;
      })

      // ========================================================================
      // ORDER CREATION
      // ========================================================================

      // Create order from session
      .addCase(createOrderFromSessionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrderFromSessionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSession = action.payload.data.session;
        state.sessionCapabilities = {
          canModifyCart: false,
          canModifyPrices: false,
          isPriceLocked: true,
          requiresApproval: false,
          canComplete: false,
          canCreateOrder: false,
        };
      })
      .addCase(createOrderFromSessionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }) // Update session notes
      .addCase(updateSessionNotesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSessionNotesThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentSession && action.payload?.data?.session) {
          state.currentSession = {
            ...state.currentSession,
            adminNotes: action.payload.data.session.adminNotes,
            // Update the entire session if needed
            ...action.payload.data.session,
          };
        }
      })

      .addCase(updateSessionNotesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get pending adjustments
      .addCase(getPendingAdjustmentsThunk.pending, (state) => {
        state.loadingPendingAdjustments = true;
        state.error = null;
      })
      .addCase(getPendingAdjustmentsThunk.fulfilled, (state, action) => {
        state.loadingPendingAdjustments = false;
        state.pendingAdjustments = action.payload.data.adjustments;
      })
      .addCase(getPendingAdjustmentsThunk.rejected, (state, action) => {
        state.loadingPendingAdjustments = false;
        state.error = action.payload;
      })

      // Reject price adjustment
      .addCase(rejectPriceAdjustmentThunk.pending, (state) => {
        state.loadingPricing = true;
        state.error = null;
      })
      .addCase(rejectPriceAdjustmentThunk.fulfilled, (state, action) => {
        state.loadingPricing = false;
        state.currentSession = action.payload.data.session;

        // Update the adjustment in local state
        if (state.currentSession?.priceAdjustments) {
          const adjIndex = state.currentSession.priceAdjustments.findIndex(
            (adj) => adj.adjustmentId === action.meta.arg.adjustmentId
          );
          if (adjIndex !== -1) {
            state.currentSession.priceAdjustments[adjIndex] = {
              ...state.currentSession.priceAdjustments[adjIndex],
              status: "REJECTED",
              approved: false,
              rejectedBy: action.meta.arg.adminCode,
              rejectedAt: new Date().toISOString(),
              rejectionReason: action.meta.arg.rejectionReason,
            };
          }
        }

        // Remove from pending adjustments
        state.pendingAdjustments = state.pendingAdjustments.filter(
          (adj) => adj.adjustmentId !== action.meta.arg.adjustmentId
        );

        // Don't need to recalculate pricing since adjustment was rejected
      })
      .addCase(rejectPriceAdjustmentThunk.rejected, (state, action) => {
        state.loadingPricing = false;
        state.error = action.payload;
      }) // Edit exchange item
      .addCase(editExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(editExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.currentSession = action.payload.data.session;
        state.exchangeItems = action.payload.data.session.exchangeItems;

        // Find and update the specific item
        const editedItem = action.payload.data.session.exchangeItems.find(
          (item) => item.exchangeItemId === action.meta.arg.exchangeItemId
        );

        if (editedItem && editedItem.status === "NEGOTIATED") {
          // Item needs re-acceptance after edit
          state.sessionCapabilities.requiresApproval = true;
        }

        // Clear pricing as values changed
        state.sessionPricing = null;
      })
      .addCase(editExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Remove exchange item
      .addCase(removeExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(removeExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.currentSession = action.payload.data.session;

        // Remove item from local state
        state.exchangeItems = state.exchangeItems.filter(
          (item) => item.exchangeItemId !== action.meta.arg.exchangeItemId
        );

        // Update session exchange items
        if (state.currentSession) {
          state.currentSession.exchangeItems =
            action.payload.data.session.exchangeItems;
        }

        // Clear pricing
        state.sessionPricing = null;
      })
      .addCase(removeExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      })

      // Unaccept exchange item
      .addCase(unacceptExchangeItemThunk.pending, (state) => {
        state.loadingExchange = true;
        state.error = null;
      })
      .addCase(unacceptExchangeItemThunk.fulfilled, (state, action) => {
        state.loadingExchange = false;
        state.currentSession = action.payload.data.session;
        state.exchangeItems = action.payload.data.session.exchangeItems;

        // Update the specific item's status
        const unacceptedItem = state.exchangeItems.find(
          (item) => item.exchangeItemId === action.meta.arg.exchangeItemId
        );

        if (unacceptedItem) {
          unacceptedItem.status = "APPRAISED";
          unacceptedItem.customerAccepted = false;
        }

        // Clear pricing as exchange value changed
        state.sessionPricing = null;
      })
      .addCase(unacceptExchangeItemThunk.rejected, (state, action) => {
        state.loadingExchange = false;
        state.error = action.payload;
      });
  },
});

export const {
  setFilters,
  resetFilters,
  setCurrentSession,
  updateSessionCapabilities,
  selectCartItem,
  selectExchangeItem,
} = consultationSlice.actions;

export default consultationSlice.reducer;
